import { concat, delay, Observable, of } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  AiChatContext,
  ChatLanguage,
  ChatListItem,
  ChatMessageRole,
  ChatMessage,
  Chat,
  CreateChatMessageReq,
  GetChatsRes,
  GetUserContextsRes,
  InternalMessageStatus,
} from '@fb/models';

import { DEFAULT_CHAT_TITLE } from '../ai-chat.constants';
import { workContextChatFields } from '../helpers/work-context';

/**
 * Quality Control user: OPERATIONAL scope + SUPPORT licenses on Work (matches /v1/chats/contexts
 * payload shape).
 */
export const getUserContextsMock = (): GetUserContextsRes => [
  { id: AiChatContext.Personal, role: 'EMPLOYEE' },
  {
    id: AiChatContext.Work,
    role: 'SUPPORT',
    workContexts: [
      { group: 'OPERATIONAL', workScope: 'QUALITY_CONTROL' },
      { group: 'SUPPORT', license: 'PINUP' },
      { group: 'SUPPORT', license: 'PINKO' },
      { group: 'SUPPORT', license: 'BOOSTWIN' },
      { group: 'SUPPORT', license: 'ZAZINO' },
    ],
  },
];

/**
 * Rich sample answer for mock mode: exercises markdown rendering — heading,
 * ordered list, inline link, image and GFM table — to verify image/table output
 * and external-link hardening locally.
 */
const V2_MOCK_RICH_ANSWER = `## How to change Payment Method in Jira

To change your Payment Method:

1. Open your **Staff** card in Jira: [Staff board](https://jira.time2go.tech/browse/STAF)
2. Click **Set Payment Method**.
3. Choose a payout method and save the changes.

![Set Payment Method location](https://placehold.co/480x240?text=Set+Payment+Method)

Carry-over rules by absence type:

| Absence Type | Carry-over | Accrues |
|---|---|---|
| Day Off | No | No |
| Family Days | No | No |
| Paid Vacation | See policy | Proportionally |

For the full rules, refer to the [Payment Info policy](https://wiki.time2go.tech/display/WORLD/Payment+Info).`;

/** Canonical business fallback from ai-hr-chatbot-2.0 — exercises paragraph breaks and inline links. */
const V2_MOCK_FALLBACK_ANSWER = `I'm designed specifically to help with RedCore workplace policies, HR procedures, and employee benefits. For other topics, please consult appropriate resources. How can I help you with RedCore-related questions?

Sorry, but I did not find an answer to this question. Try to formulate it differently or ask another question.

If none of these work, please contact the PP of your segment from [list](https://wiki.time2go.tech/display/WORLD/People+Partners)

If your question concerns finances, please submit a financial request via [link](https://example.test/financial-request)`;

const V2_MOCK_FALLBACK_LINKS: ChatMessage['links'] = [
  {
    label: 'list',
    url: 'https://wiki.time2go.tech/display/WORLD/People+Partners',
    normalizedUrl: 'https://wiki.time2go.tech/display/WORLD/People+Partners',
  },
  {
    label: 'link',
    url: 'https://example.test/financial-request',
    normalizedUrl: 'https://example.test/financial-request',
  },
];

/** Structured citations paired with the rich mock answer. */
const V2_MOCK_SOURCES: ChatMessage['sources'] = [
  {
    pageTitle: 'How to add Payment Info',
    pageUrl: 'https://wiki.time2go.tech/display/WORLD/How+to+add+Payment+Info',
    spaceKey: 'WORLD',
  },
  {
    pageTitle: 'Staff board',
    pageUrl: 'https://jira.time2go.tech/browse/STAF',
    spaceKey: 'WORLD',
  },
];

/** Structured inline links (also embedded in `content`) paired with the rich mock answer. */
const V2_MOCK_LINKS: ChatMessage['links'] = [
  {
    label: 'Payment Info policy',
    url: 'https://wiki.time2go.tech/display/WORLD/Payment+Info',
    normalizedUrl: 'https://wiki.time2go.tech/display/WORLD/Payment+Info',
    kind: 'document',
    sourcePageId: '238852592',
  },
];

/** Simulates POST /v1/chats/messages. Returns the AGENT reply. */
export const createChatMessageMock = (req: CreateChatMessageReq): ChatMessage => {
  const chatId = req.chatId ?? uuid();
  const now = new Date().toISOString();

  if (!v2MockChatRegistry.has(chatId)) {
    v2MockFirstUserMessage.set(chatId, req.content);
    v2MockChatRegistry.set(chatId, {
      id: chatId,
      userEmail: 'user@example.com',
      title: DEFAULT_CHAT_TITLE,
      isPinned: false,
      createdAt: now,
      updatedAt: now,
      ...(req.context != null ? { context: req.context } : {}),
      ...workContextChatFields(req.workContext),
    });
  } else {
    const existing = v2MockChatRegistry.get(chatId)!;
    v2MockChatRegistry.set(chatId, { ...existing, updatedAt: now });
  }

  return {
    id: uuid(),
    chatId,
    role: ChatMessageRole.Agent,
    content: /fallback/i.test(req.content) ? V2_MOCK_FALLBACK_ANSWER : V2_MOCK_RICH_ANSWER,
    language: ChatLanguage.En,
    createdAt: now,
    updatedAt: now,
    sources: /fallback/i.test(req.content) ? [] : V2_MOCK_SOURCES,
    links: /fallback/i.test(req.content) ? V2_MOCK_FALLBACK_LINKS : V2_MOCK_LINKS,
    internalStatus: InternalMessageStatus.resolved,
  };
};

const V2_MOCK_STREAM_CHUNK_SIZE = 24;
const V2_MOCK_STREAM_CHUNK_DELAY_MS = 40;

/** Simulates SSE create-message: provisional snapshots, then the persisted AGENT reply. */
export const createChatMessageStreamMock = (
  req: CreateChatMessageReq,
): Observable<ChatMessage> => {
  const finalMessage = createChatMessageMock(req);
  const chunks: string[] = [];

  for (
    let index = 0;
    index < finalMessage.content.length;
    index += V2_MOCK_STREAM_CHUNK_SIZE
  ) {
    chunks.push(
      finalMessage.content.slice(index, index + V2_MOCK_STREAM_CHUNK_SIZE),
    );
  }

  let answer = '';
  const partials = chunks.map((chunk, index) => {
    answer += chunk;

    return of({
      id: '',
      chatId: finalMessage.chatId,
      role: ChatMessageRole.Agent,
      content: answer,
      language: ChatLanguage.En,
      createdAt: finalMessage.createdAt,
      updatedAt: finalMessage.updatedAt,
      isStreaming: true,
      isFeedbackEligible: false,
    } satisfies ChatMessage).pipe(
      delay(V2_MOCK_STREAM_CHUNK_DELAY_MS * (index + 1)),
    );
  });

  return concat(
    of({
      id: '',
      chatId: finalMessage.chatId,
      role: ChatMessageRole.Agent,
      content: '',
      language: ChatLanguage.En,
      createdAt: finalMessage.createdAt,
      updatedAt: finalMessage.updatedAt,
      isStreaming: true,
      isFeedbackEligible: false,
    } satisfies ChatMessage),
    ...partials,
    of({ ...finalMessage, isStreaming: false }).pipe(
      delay(V2_MOCK_STREAM_CHUNK_DELAY_MS * (chunks.length + 1)),
    ),
  );
};

/** Module-level mock registry: chatId → ChatListItem summary. */
const v2MockFirstUserMessage = new Map<string, string>();
const v2MockChatRegistry = new Map<string, ChatListItem>([
  [
    'mock-chat-1',
    {
      id: 'mock-chat-1',
      userEmail: 'user@example.com',
      title: 'Vacation days remaining',
      isPinned: false,
      context: AiChatContext.Personal,
      createdAt: '2026-05-20T09:00:00.000Z',
      updatedAt: '2026-05-20T09:05:00.000Z',
    },
  ],
  [
    'mock-chat-2',
    {
      id: 'mock-chat-2',
      userEmail: 'user@example.com',
      title: 'Find relocation policy',
      isPinned: false,
      context: AiChatContext.Personal,
      createdAt: '2026-05-19T14:00:00.000Z',
      updatedAt: '2026-05-19T14:10:00.000Z',
    },
  ],
  [
    'mock-chat-3',
    {
      id: 'mock-chat-3',
      userEmail: 'user@example.com',
      title: 'How do I request sick leave?',
      isPinned: false,
      context: AiChatContext.Personal,
      createdAt: '2026-05-18T11:00:00.000Z',
      updatedAt: '2026-05-18T11:08:00.000Z',
    },
  ],
  [
    'mock-work-chat-1',
    {
      id: 'mock-work-chat-1',
      userEmail: 'user@example.com',
      title: 'Login error COM (Pin-up)',
      isPinned: false,
      context: AiChatContext.Work,
      license: 'PINUP',
      createdAt: '2026-05-20T14:00:00.000Z',
      updatedAt: '2026-05-20T14:01:00.000Z',
    },
  ],
  [
    'mock-work-chat-2',
    {
      id: 'mock-work-chat-2',
      userEmail: 'user@example.com',
      title: 'Payment processing issue BoostWin',
      isPinned: false,
      context: AiChatContext.Work,
      license: 'BOOSTWIN',
      createdAt: '2026-05-19T10:00:00.000Z',
      updatedAt: '2026-05-19T10:05:00.000Z',
    },
  ],
  [
    'mock-work-chat-3',
    {
      id: 'mock-work-chat-3',
      userEmail: 'user@example.com',
      title: 'Call scoring checklist',
      isPinned: false,
      context: AiChatContext.Work,
      workScope: 'QUALITY_CONTROL',
      createdAt: '2026-05-18T09:00:00.000Z',
      updatedAt: '2026-05-18T09:05:00.000Z',
    },
  ],
]);

/** Simulates POST /v1/chats/{chatId}/title. Assigns a title from the user message. */
export const generateChatTitleMock = (chatId: string, content?: string): ChatListItem | null => {
  const existing = v2MockChatRegistry.get(chatId);

  if (!existing) {
    return null;
  }

  if (existing.title !== DEFAULT_CHAT_TITLE) {
    return existing;
  }

  const source = content || v2MockFirstUserMessage.get(chatId) || existing.title;
  const title = source.split(/\s+/).slice(0, 7).join(' ').slice(0, 100) || DEFAULT_CHAT_TITLE;
  const updated: ChatListItem = {
    ...existing,
    title,
    updatedAt: new Date().toISOString(),
  };
  v2MockChatRegistry.set(chatId, updated);

  return updated;
};

/** Simulates PATCH /v1/chats/{chatId}. Updates the chat title. */
export const renameChatMock = (chatId: string, title: string): ChatListItem => {
  const now = new Date().toISOString();
  const existing = v2MockChatRegistry.get(chatId);
  const updated: ChatListItem = {
    ...existing,
    id: chatId,
    userEmail: existing?.userEmail ?? 'user@example.com',
    title,
    isPinned: existing?.isPinned ?? false,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  v2MockChatRegistry.set(chatId, updated);

  return updated;
};

/** Simulates DELETE /v1/chats/{chatId}. Removes the chat from the mock registry. */
export const deleteChatMock = (chatId: string): void => {
  v2MockChatRegistry.delete(chatId);
};

/** Simulates PATCH /v1/chats/{chatId} for pin/unpin. Updates the isPinned flag in the mock registry. */
export const pinChatMock = (chatId: string, isPinned: boolean): ChatListItem => {
  const now = new Date().toISOString();
  const existing = v2MockChatRegistry.get(chatId);
  const updated: ChatListItem = {
    ...existing,
    id: chatId,
    userEmail: existing?.userEmail ?? 'user@example.com',
    title: existing?.title ?? '',
    isPinned,
    createdAt: existing?.createdAt ?? now,
    updatedAt: existing?.updatedAt ?? now,
  };
  v2MockChatRegistry.set(chatId, updated);

  return updated;
};

/** Simulates GET /v1/chats. Returns chats filtered by context (if provided), pinned first, then by recent activity. */
export const getChatsMock = (query?: { context?: AiChatContext }): GetChatsRes => {
  const all = [...v2MockChatRegistry.values()];
  const filtered = query?.context
    ? all.filter(chat => chat.context === query.context)
    : all;

  const items = filtered.sort((a, b) => {
    if (a.isPinned !== b.isPinned) {
      return a.isPinned ? -1 : 1;
    }

    return b.updatedAt.localeCompare(a.updatedAt);
  });

  return {
    items,
    total: items.length,
    page: 1,
    size: 20,
    pages: 1,
  };
};

/** Simulates GET /v1/chats/{chatId}. Returns the chat with a mock message list. */
export const getChatMock = (chatId: string): Chat => {
  const meta = v2MockChatRegistry.get(chatId);
  const now = new Date().toISOString();

  return {
    id: chatId,
    userEmail: 'user@example.com',
    title: meta?.title ?? 'Chat',
    isPinned: false,
    context: meta?.context,
    license: meta?.license,
    workScope: meta?.workScope,
    createdAt: meta?.createdAt ?? now,
    updatedAt: meta?.updatedAt ?? now,
    messages: [
      {
        id: uuid(),
        chatId,
        role: ChatMessageRole.User,
        content: meta?.title ?? 'Hello',
        language: ChatLanguage.Ru,
        createdAt: meta?.createdAt ?? now,
        updatedAt: meta?.createdAt ?? now,
      },
      {
        id: uuid(),
        chatId,
        role: ChatMessageRole.Agent,
        content: V2_MOCK_RICH_ANSWER,
        language: ChatLanguage.En,
        createdAt: meta?.updatedAt ?? now,
        updatedAt: meta?.updatedAt ?? now,
        sources: V2_MOCK_SOURCES,
        links: V2_MOCK_LINKS,
      },
    ],
  };
};
